import { Box, Button, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArchiveIcon from "@mui/icons-material/Archive";
import RemovePermitPackage from "../../modals/RemovePermitPackage";
import React, { useState } from "react";
import AddSinglePermitDocumentModal from "../../modals/AddSinglePermitDocumentModal";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import ChangePackageNameModal from "../../modals/ChangePackageNameModal";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { useTour } from "@reactour/tour";
import { useLocation } from "react-router-dom";
import { getPermitPackages } from "../../../store/actions/dataActions";
import ChangeEmailModal from "../../modals/ChangeEmailModal";
import {
  setMyPermitsSnackOpen,
  setPage,
  setRowCount,
  setSelectedRow,
} from "../../../store/actions/uiActions/myPermitsActions";

const PermitSettings = ({ selectedPermitType, isReadOnlyPage }) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const { selectedRow } = useSelector((state) => state.myPermitsComponent);

  const location = useLocation();
  const dispatch = useDispatch();
  const { isOpen } = useTour();

  const { processing_permit } = selectedRow;

  const [isCooldown, setIsCooldown] = useState(false);
  const [removePermitOpen, setRemovePermitOpen] = useState(false);
  const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const [changeEmailModalOpen, setChangeEmailModalOpen] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newEmailLoading, setNewEmailLoading] = useState(false);
  const [emailChangeText, setEmailChangeText] = useState("");
  const [newEmailError, setNewEmailError] = useState("");
  const [sendReminder, setSendReminder] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [editPNameOpen, setEditPNameOpen] = useState(false);

  // Check if we're trying to remove the main permit while additional permits exist
  const isMainPermitWithAdditionals =
    selectedPermitType === selectedRow?.permit_type &&
    selectedRow?.additional_permits &&
    Object.keys(selectedRow.additional_permits).length > 0;

  // Check if this is an additional permit
  const isAdditionalPermit =
    selectedPermitType !== selectedRow?.permit_type &&
    selectedRow?.additional_permits &&
    selectedRow.additional_permits[selectedPermitType];

  const handleArchive = async () => {
    try {
      setArchiveLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/archive-package`,
        {
          task_id: selectedRow?.task_id,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );
      if (resp.data.success === true) {
        const result = await dispatch(
          getPermitPackages({ newPage: 0, newPageSize: 5 })
        );
        await dispatch(setSelectedRow(null));
        dispatch(setPage(0));
        dispatch(setRowCount(result?.data || 0));
      }
    } catch (error) {
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleSendOwnerReminder = async () => {
    if (isOpen || reminderLoading || isCooldown) {
      return;
    }
    try {
      setReminderLoading(true);
      setIsCooldown(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/send-signee-register-reminder`,
        {
          task_id: selectedRow?.task_id,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );
      if (resp.data.success === true) {
        dispatch(
          setMyPermitsSnackOpen({
            isOpen: true,
            snackText:
              "The email reminder to the property owner has been sent successfully.",
          })
        );
      }
    } catch (error) {
    } finally {
      setReminderLoading(false);
      setTimeout(() => {
        setIsCooldown(false);
      }, 60000);
    }
  };

  const handleChangeEmail = async () => {
    try {
      setNewEmailLoading(true);

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/change-owner-email`,
        {
          task_id: selectedRow?.task_id,
          newEmail: newEmail,
          sendReminder: sendReminder,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
        }
      );
      if (resp?.data?.success) {
        setEmailChangeText(resp?.data?.message);
        setTimeout(() => {
          setChangeEmailModalOpen(false);
          setEmailChangeText("");
          setNewEmail("");
          setNewEmailError("");
          setNewEmailLoading(false);
        }, 3000);
      }
    } catch (error) {
      setEmailChangeText("An error occurred. Please try again.");
      setNewEmailLoading(false);
    }
  };

  const calculateEmailDisabled = () => {
    if (processing_permit?.notarization?.owner?.notarization?.only_sign) {
      return true;
    }
    const contractorNotarization =
      processing_permit?.notarization?.contractor?.notarization;
    const ownerNotarization =
      processing_permit?.notarization?.owner?.notarization;

    if (
      ownerNotarization?.notary_failed === true ||
      contractorNotarization?.notary_failed === true
    ) {
      return true;
    }

    if (
      processing_permit?.notarization?.finalized === true ||
      !processing_permit?.notarization?.started
    ) {
      return true;
    }
    if (processing_permit?.notarization?.signing_order === "contractor") {
      if (contractorNotarization?.is_notarized === false) {
        return true;
      } else if (
        contractorNotarization?.is_notarized === true &&
        ownerNotarization?.waiting_to_schedule === false
      ) {
        return true;
      }
      return false;
    } else if (processing_permit?.notarization?.signing_order === "owner") {
      if (ownerNotarization?.is_notarized === true) {
        return true;
      } else if (ownerNotarization?.waiting_to_schedule === false) {
        return true;
      }
      return false;
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mt: 1,
        }}
      >
        <img
          alt="settings"
          src="/img/website_img/settings.png"
          style={{
            width: "100%",
            maxWidth: "5rem",
            height: "auto",
            borderRadius: "20px",
          }}
        />
      </Box>
      <Box
        mt={3}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          px: 1,
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {!isReadOnlyPage && (
          <>
            {location.pathname !== "/permits/invitations" && (
              <Button
                onClick={() => {
                  if (isOpen) {
                    return;
                  }
                  setAddDocumentModalOpen(true);
                }}
                disabled={processing_permit?.notarization?.started}
                startIcon={<AddIcon style={{ fontSize: "30px" }} />}
              >
                Add Document
              </Button>
            )}

            <Button
              onClick={() => {
                if (isOpen) {
                  return;
                }
                setEditPNameOpen(true);
              }}
              disabled={processing_permit?.notarization?.started}
              color="primary"
              startIcon={<EditIcon style={{ fontSize: "26px" }} />}
            >
              Edit Package Name
            </Button>
            <LoadingButton
              loading={reminderLoading}
              onClick={handleSendOwnerReminder}
              disabled={calculateEmailDisabled() || isCooldown}
              color="primary"
              startIcon={
                !reminderLoading ? (
                  <EmailOutlinedIcon style={{ fontSize: "26px" }} />
                ) : undefined
              }
            >
              Send Reminder Email
            </LoadingButton>
            <LoadingButton
              onClick={() => {
                setChangeEmailModalOpen(true);
              }}
              disabled={calculateEmailDisabled()}
              color="warning"
              startIcon={<EditIcon style={{ fontSize: "26px" }} />}
            >
              Change Invitation Email
            </LoadingButton>
            {selectedRow?.notarized &&
            selectedRow?.recorded &&
            !selectedRow?.archived_package?.is_archived &&
            (selectedRow?.permit_package_created ||
              (!selectedRow?.permit_package_created &&
                selectedRow?.selected_documents?.length === 1)) ? (
              <LoadingButton
                loading={archiveLoading}
                onClick={handleArchive}
                startIcon={<ArchiveIcon style={{ fontSize: "26px" }} />}
              >
                Archive Permit Package
              </LoadingButton>
            ) : (
              <Button
                disabled
                startIcon={<ArchiveIcon style={{ fontSize: "26px" }} />}
              >
                Archive Permit Package
              </Button>
            )}
          </>
        )}

        {location.pathname !== "/permits/invitations" && (
          <Tooltip
            title={
              isMainPermitWithAdditionals
                ? "Please Remove Additional Permits First"
                : ""
            }
          >
            <span>
              <Button
                onClick={() => {
                  if (isOpen) {
                    return;
                  }
                  setRemovePermitOpen(true);
                }}
                disabled={
                  processing_permit?.notarization?.started ||
                  (selectedRow?.free_addition?.noc === true &&
                    selectedRow?.noc_created) ||
                  (selectedRow?.free_addition?.pp === true &&
                    selectedRow?.noc_created) ||
                  selectedRow?.payments?.vip?.paid === true ||
                  selectedRow?.payments?.create_noc?.paid === true ||
                  selectedRow?.payments?.create_permit_package?.paid === true ||
                  isMainPermitWithAdditionals // Disable if trying to remove main permit with additional permits
                }
                color="error"
                startIcon={<DeleteIcon style={{ fontSize: "28px" }} />}
              >
                {isAdditionalPermit
                  ? "Remove Additional Permit"
                  : "Remove Permit Package"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
      <RemovePermitPackage
        open={removePermitOpen}
        setOpen={setRemovePermitOpen}
        selectedPermitType={selectedPermitType}
        isAdditionalPermit={isAdditionalPermit}
        isReadOnlyPage={isReadOnlyPage}
      />
      {!isReadOnlyPage && (
        <>
          <ChangePackageNameModal
            open={editPNameOpen}
            setOpen={setEditPNameOpen}
          />
          <ChangeEmailModal
            open={changeEmailModalOpen}
            setOpen={setChangeEmailModalOpen}
            onConfirm={handleChangeEmail}
            newEmail={newEmail}
            setNewEmail={setNewEmail}
            newEmailError={newEmailError}
            setNewEmailError={setNewEmailError}
            newEmailLoading={newEmailLoading}
            sendReminder={sendReminder}
            setSendReminder={setSendReminder}
            emailChangeText={emailChangeText}
          />
          <AddSinglePermitDocumentModal
            open={addDocumentModalOpen}
            setOpen={setAddDocumentModalOpen}
          />
        </>
      )}
    </>
  );
};

export default React.memo(PermitSettings);
